/**
 * Podium scene — end-of-game celebration. Top 5 players ranked, winner row
 * scaled up with celebration accent. Confetti is fired by main.ts on scene
 * enter; this file owns the layout and row treatment.
 */

import type { Podium } from '../state/types.js';

export interface PodiumHandle {
  el: HTMLDivElement;
  setPodium(p: Podium[]): void;
  setVisible(v: boolean): void;
}

const STYLES = `
  @keyframes hr-podium-row-in {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes hr-podium-glow {
    0%, 100% { box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 0 50px rgba(34,197,94,0.4); }
    50%      { box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 0 90px rgba(34,197,94,0.7); }
  }
  .hr-podium {
    position: absolute; inset: 0;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 60px;
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    z-index: 2;
  }
  .hr-podium-cup {
    font-size: 100px; line-height: 1; margin-bottom: 12px;
    filter: drop-shadow(0 8px 24px rgba(0,0,0,0.6));
  }
  .hr-podium-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 160px; letter-spacing: 0.12em;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 8px 0 var(--sg-secondary, #3B0764), 0 0 80px rgba(255,196,61,0.4);
    line-height: 0.95;
  }
  .hr-podium-sub {
    font-family: 'Oswald', sans-serif;
    font-size: 24px; letter-spacing: 0.32em;
    color: var(--sg-accent, #22C55E);
    margin-top: 4px; margin-bottom: 36px;
    text-transform: uppercase;
    text-shadow: 0 0 18px rgba(34,197,94,0.4);
  }
  .hr-podium-list {
    display: flex; flex-direction: column; gap: 14px;
    min-width: 760px;
    width: min(900px, 60vw);
  }
  .hr-podium-row {
    display: grid; grid-template-columns: 70px 1fr 200px;
    gap: 18px; align-items: center;
    padding: 18px 28px;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(15,23,42,0.85) 0%, rgba(3,6,23,0.95) 100%);
    border: 2px solid rgba(255,196,61,0.4);
    font-size: 32px;
    box-shadow: 0 8px 28px rgba(0,0,0,0.55);
    animation: hr-podium-row-in 380ms ease-out backwards;
  }
  .hr-podium-row:nth-child(1) { animation-delay: 80ms; }
  .hr-podium-row:nth-child(2) { animation-delay: 180ms; }
  .hr-podium-row:nth-child(3) { animation-delay: 280ms; }
  .hr-podium-row:nth-child(4) { animation-delay: 380ms; }
  .hr-podium-row:nth-child(5) { animation-delay: 480ms; }
  .hr-podium-row[data-rank="1"] {
    padding: 28px 36px;
    font-size: 56px;
    background: linear-gradient(180deg, rgba(34,197,94,0.7) 0%, rgba(22,128,71,0.9) 100%);
    border: 3px solid #fff;
    animation: hr-podium-row-in 460ms cubic-bezier(0.34,1.56,0.64,1) backwards,
               hr-podium-glow 2.4s ease-in-out 800ms infinite;
    grid-template-columns: 100px 1fr 280px;
  }
  .hr-podium-rank {
    font-family: 'Bebas Neue', sans-serif;
    color: var(--sg-primary, #FFC43D);
    text-align: center;
    line-height: 1;
    font-size: 1.4em;
    text-shadow: 0 4px 0 rgba(0,0,0,0.6);
  }
  .hr-podium-row[data-rank="1"] .hr-podium-rank { color: #fff; }
  .hr-podium-name {
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
  }
  .hr-podium-points {
    font-family: 'Oswald', sans-serif;
    font-weight: 700;
    text-align: right;
    color: var(--sg-primary, #FFC43D);
    font-variant-numeric: tabular-nums;
    text-shadow: 0 2px 0 rgba(0,0,0,0.4);
  }
  .hr-podium-row[data-rank="1"] .hr-podium-points {
    color: #fff;
    text-shadow: 0 2px 0 rgba(0,0,0,0.6);
  }
  .hr-podium-empty {
    opacity: 0.7; font-size: 32px; text-align: center;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('hr-podium-style')) return;
  const s = document.createElement('style');
  s.id = 'hr-podium-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createPodium(stage: HTMLElement): PodiumHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'hr-podium';
  el.setAttribute('data-scene', 'podium');
  el.setAttribute('data-testid', 'hr-podium');

  const cup = document.createElement('div');
  cup.className = 'hr-podium-cup';
  cup.textContent = '\uD83C\uDFC6';
  const title = document.createElement('div');
  title.className = 'hr-podium-title';
  title.textContent = 'GAME OVER';
  const sub = document.createElement('div');
  sub.className = 'hr-podium-sub';
  sub.textContent = 'FINAL STANDINGS';

  const list = document.createElement('div');
  list.className = 'hr-podium-list';
  list.setAttribute('data-testid', 'hr-podium-list');

  el.appendChild(cup);
  el.appendChild(title);
  el.appendChild(sub);
  el.appendChild(list);
  stage.appendChild(el);

  return {
    el,
    setPodium(rows) {
      list.innerHTML = '';
      if (rows.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'hr-podium-empty';
        empty.textContent = 'No scores this game.';
        list.appendChild(empty);
        return;
      }
      for (const r of rows.slice(0, 5)) {
        const row = document.createElement('div');
        row.className = 'hr-podium-row';
        row.setAttribute('data-rank', String(r.rank));
        const rankEl = document.createElement('div');
        rankEl.className = 'hr-podium-rank';
        rankEl.textContent = `#${r.rank}`;
        const nameEl = document.createElement('div');
        nameEl.className = 'hr-podium-name';
        nameEl.textContent = r.display_name;
        const ptsEl = document.createElement('div');
        ptsEl.className = 'hr-podium-points';
        ptsEl.textContent = `${r.total_points.toLocaleString()} pts`;
        row.appendChild(rankEl);
        row.appendChild(nameEl);
        row.appendChild(ptsEl);
        list.appendChild(row);
      }
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
    },
  };
}
